import { useState } from "react";
import { ChevronDown, BadgeCheck } from "lucide-react";
import { useTrustpilotStats } from "@/hooks/useTrustpilotStats";

const TrustpilotStars = ({ rating, size = 4 }: { rating: number; size?: number }) => {
  const starSize = size * 4;
  return (
    <div className="flex gap-[2px]">
      {[...Array(5)].map((_, i) => {
        const fillPercent = Math.min(100, Math.max(0, (rating - i) * 100));
        const isFull = fillPercent >= 100;
        return (
          <div key={i} className="relative flex-shrink-0" style={{ width: starSize, height: starSize }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 96 96" preserveAspectRatio="xMidYMid meet">
              <rect fill="#DCDCE6" width="96" height="96" />
              <path d="M48,64.7 L62.6,61 L68.7,79.8 L48,64.7 Z M81.6,40.4 L55.9,40.4 L48,16.2 L40.1,40.4 L14.4,40.4 L35.2,55.4 L27.3,79.6 L48.1,64.6 L60.9,55.4 L81.6,40.4 Z" fill="#FFFFFF" />
            </svg>
            {fillPercent > 0 && (
              <div className="absolute top-0 left-0 h-full overflow-hidden" style={{ width: isFull ? '100%' : `${fillPercent}%` }}>
                <svg className="h-full" viewBox="0 0 96 96" preserveAspectRatio="xMinYMid slice" style={{ width: starSize }}>
                  <rect fill="#00B67A" width="96" height="96" />
                  <path d="M48,64.7 L62.6,61 L68.7,79.8 L48,64.7 Z M81.6,40.4 L55.9,40.4 L48,16.2 L40.1,40.4 L14.4,40.4 L35.2,55.4 L27.3,79.6 L48.1,64.6 L60.9,55.4 L81.6,40.4 Z" fill="#FFFFFF" />
                </svg>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const PillTrustpilotWidget = () => {
  const { reviewCount, rating, reviews, loading } = useTrustpilotStats();
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentReview, setCurrentReview] = useState(0);

  const currentReviewData = reviews[currentReview];

  return (
    <div className="max-w-sm lg:max-w-md">
      <div
        className={`bg-card/90 backdrop-blur-sm shadow-sm transition-all duration-700 ${
          isExpanded ? "rounded-2xl shadow-card w-full" : "rounded-xl w-full"
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)" }}
      >
        {/* Collapsed header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-full inline-flex items-center gap-3 px-4 py-2.5 transition-all duration-300 hover:bg-card ${
            isExpanded ? "justify-center border-b border-border/40 rounded-t-2xl" : "rounded-xl"
          }`}
        >
          <img src="https://cdn.trustpilot.net/brand-assets/4.1.0/logo-black.svg" alt="Trustpilot" className="h-4" loading="lazy" />
          <div className="w-px h-4 bg-border" />
          <TrustpilotStars rating={rating ?? 4.8} size={4} />
          <span className="text-sm font-semibold text-foreground">{(rating ?? 4.8).toFixed(1)}</span>
          <span className="text-xs text-muted-foreground">({reviewCount ? reviewCount.toLocaleString() : "…"})</span>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-300 ${isExpanded ? "rotate-180" : "animate-bounce-down"}`} />
        </button>

        {/* Expanded review carousel */}
        <div
          className={`overflow-hidden transition-all duration-700 ${isExpanded ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"}`}
          style={{ transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)" }}
        >
          <div className="p-4 pt-3">
            {reviews.length > 0 && currentReviewData && (
              <div className="min-h-[140px]">
                <div className="flex items-center gap-2 mb-2">
                  <TrustpilotStars rating={currentReviewData.rating} size={3} />
                  {currentReviewData.verified && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium bg-emerald-50 px-1.5 py-0.5 rounded-full">
                      <BadgeCheck className="w-3 h-3" /> Verified
                    </span>
                  )}
                </div>
                <h4 className="font-semibold text-foreground text-sm mb-1">{currentReviewData.title}</h4>
                <p className="text-muted-foreground text-xs leading-relaxed line-clamp-4">{currentReviewData.text}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs font-medium text-foreground">{currentReviewData.author}</span>
                </div>
              </div>
            )}
            <div className="flex justify-center gap-1.5 mt-4 pt-3 border-t border-border/40">
              {reviews.map((_, idx) => (
                <button key={idx} onClick={() => setCurrentReview(idx)} className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${idx === currentReview ? "bg-primary w-4" : "bg-muted-foreground/20"}`} />
              ))}
            </div>
            <a href="https://uk.trustpilot.com/review/contraceptiondirect.uk" target="_blank" rel="noopener noreferrer" className="block text-center text-xs text-primary font-medium mt-3 hover:underline">
              View all reviews on Trustpilot →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PillTrustpilotWidget;
